import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';

export function useLinks() {
  const { user } = useAuth();
  const [allLinks, setAllLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.uid) {
      setAllLinks([]);
      setLoading(false);
      return;
    }

    // Query links by userId (multi-tenant)
    const q = query(
      collection(db, 'links'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const linksData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setAllLinks(linksData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching links:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // All links belong to current user now (no need for visibility filtering)
  const visibleLinks = allLinks;

  const addLink = async (linkData) => {
    if (!user?.uid) {
      throw new Error('User must be logged in to add links');
    }

    try {
      await addDoc(collection(db, 'links'), {
        ...linkData,
        userId: user.uid, // Multi-tenant: owner by Firebase UID
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error adding link:', err);
      throw err;
    }
  };

  const updateLink = async (id, linkData) => {
    try {
      const linkRef = doc(db, 'links', id);
      await updateDoc(linkRef, {
        ...linkData,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error updating link:', err);
      throw err;
    }
  };

  const deleteLink = async (id) => {
    try {
      await deleteDoc(doc(db, 'links', id));
    } catch (err) {
      console.error('Error deleting link:', err);
      throw err;
    }
  };

  const togglePin = async (id, currentPinned) => {
    try {
      const linkRef = doc(db, 'links', id);
      await updateDoc(linkRef, {
        pinned: !currentPinned,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error toggling pin:', err);
      throw err;
    }
  };

  const archiveLink = async (id) => {
    try {
      const linkRef = doc(db, 'links', id);
      await updateDoc(linkRef, {
        archived: true,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error archiving link:', err);
      throw err;
    }
  };

  const unarchiveLink = async (id) => {
    try {
      const linkRef = doc(db, 'links', id);
      await updateDoc(linkRef, {
        archived: false,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error unarchiving link:', err);
      throw err;
    }
  };

  // Separate active and archived links
  const activeLinks = visibleLinks.filter(l => !l.archived);
  const archivedLinks = visibleLinks.filter(l => l.archived);

  return {
    links: activeLinks,
    archivedLinks,
    loading,
    error,
    addLink,
    updateLink,
    deleteLink,
    togglePin,
    archiveLink,
    unarchiveLink
  };
}
